import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Stack, { StackProps } from '@mui/material/Stack';
import { RootStoreType } from 'src/types/store';
import { styled } from '@mui/material/styles';
import { WarningIcon, CautionIcon, CloseIcon } from 'src/components/SkladonIcon/SmallIcons';
import { colors, sizes } from 'src/themes/default';
import IconButton from '@mui/material/IconButton';
import { removeMessage } from 'src/resources/alertMessages/reducer';
import { AlertMessageType } from 'src/types/alertMessages';

const TIMEOUT_CLOSE = 5000;

const Container = styled(Stack)<StackProps>(({ theme }) => ({
    position: 'fixed',
    right: '16px',
    top: '16px',
    width: '400px',
    zIndex: 10000,

    [theme.breakpoints.down('sm')]: {
        width: 'unset',
        maxWidth: 'calc(100% - 30px)',
    },

    '& .MuiAlert-root': {
        borderRadius: sizes.radius,
        position: 'relative',

        '&.MuiAlert-standardSuccess': {
            backgroundColor: colors.accent_D3,
        },

        '&.MuiAlert-standardError': {
            backgroundColor: colors.alert_O,
            color: colors.primary_W,

            '& svg path': {
                fill: colors.primary_W,
            },

            '& .MuiAlertTitle-root': {
                color: colors.primary_W,
            },
        },

        '& .MuiAlertTitle-root': {
            fontSize: sizes.small_FS,
            lineHeight: '30px',
        },

        '& svg': {
            width: '27px',
            height: '27px',
        },

        '& .MuiAlert-message': {
            width: '280px',
            paddingBottom: '0px',
        },

        '& .MuiButtonBase-root': {
            position: 'absolute',
            top: '5px',
            right: '16px',
        },
    },
}));

const AlertText = styled('div')({
    paddingBottom: '8px',
});

const Timer = styled('div')<{
    type: 'success' | 'error' | 'warning';
}>(({ theme, type }) => ({
    position: 'absolute',
    backgroundColor: type === 'error' ? colors.alert_D2 : type === 'success' ? colors.accent_D2 : colors.warning_D2,
    bottom: '0px',
    left: '0px',
    right: '0px',
    zIndex: 2,
    height: '4px',
}));

const TimerProgres = styled('div')<{
    type: 'success' | 'error' | 'warning';
}>(({ theme, type }) => ({
    position: 'absolute',
    backgroundColor: type === 'error' ? colors.alert_V2 : type === 'success' ? colors.accent_O : colors.warning_D1,
    bottom: '0px',
    left: '0px',
    right: '0%',
    zIndex: 2,
    height: '4px',
    animationName: 'loverRight',
    animationTimingFunction: 'linear',
    animationDuration: `${TIMEOUT_CLOSE}ms`,

    '@keyframes loverRight': {
        '0%': {
            right: '100%',
        },
        '100%': {
            right: '0%',
        },
    },
}));

const MessageItem: FC<{ message: AlertMessageType }> = (props) => {
    const dispatch = useDispatch();

    const [removeTimeout, setRemoveTimeout] = useState<NodeJS.Timeout | null>(null);
    const [showProgress, setShowProgress] = useState(true);

    useEffect(() => {
        resetTimeout();
        return () => {
            if (removeTimeout) clearTimeout(removeTimeout);
        };
    }, [props.message]);

    const resetTimeout = () => {
        if (removeTimeout) clearTimeout(removeTimeout);
        const timeout = setTimeout(() => {
            handleClose();
        }, TIMEOUT_CLOSE);
        setRemoveTimeout(timeout);
    };

    const handleClose = () => {
        dispatch(removeMessage(props.message.id));
    };

    const onMouseEnter = () => {
        setShowProgress(false);
        if (removeTimeout) clearTimeout(removeTimeout);
    };

    const onMouseLeave = () => {
        setShowProgress(true);
        resetTimeout();
    };

    let icon = <CautionIcon />;
    switch (props.message.type) {
        case 'error':
            icon = <WarningIcon />;
            break;
        default:
            break;
    }
    return (
        <Alert severity={props.message.type} icon={icon} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <AlertTitle>{props.message.title}</AlertTitle>
            {props.message.text && <AlertText>{props.message.text}</AlertText>}
            <IconButton color="primary" onClick={() => handleClose()}>
                <input hidden accept="image/*" type="file" />
                <CloseIcon />
            </IconButton>
            <Timer type={props.message.type}>{showProgress && <TimerProgres type={props.message.type} />}</Timer>
        </Alert>
    );
};

const AlertMessagesContainer: FC = () => {
    const messages = useSelector((state: RootStoreType) => state.alertMessages.messages);

    return (
        <Container spacing={2}>
            {messages.map((message) => {
                return <MessageItem key={message.id} message={message} />;
            })}
        </Container>
    );
};

export default AlertMessagesContainer;
